import {Button, Card, Flex, Input, Space} from "antd";
import Title from "antd/es/typography/Title";
import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowDown, faArrowUp, faArrowUpWideShort, faRotate, faTrash} from "@fortawesome/free-solid-svg-icons";
import {generateSectionPrompt, generateSectionsPrompt, improveSectionPrompt} from "./prompts/PlotSectionsPrompts";
import {aiChainPrompt, similaritySearch} from "../../../../../../ai/Prompt";
import {DraftChapter, DraftSection} from "../../../../../../feature/Draft";
import {useAppDispatch, useAppSelector} from "../../../../../../feature/tools";
import {updateChapter, updateScene, updateSection} from "../../../../../../feature/project/draftSlice";
import {TextAreaActions} from "../../../../../../ai/components/TextAreaActions";
import {GenButton} from "../../../../../../ai/components/GenButton";
import {DebounceInput} from "../../../../../../components/DebounceInput";
import {IconBoltGrad} from "../../../../../../components/icons/faBoltGrad";
import {BoltIcon} from "../../../../../../ai/components/BoltIcons";

export const PlotChapterDetails = (props: { chapter: DraftChapter }) => {
    const dispatch = useAppDispatch()
    const draft = useAppSelector(state => state.draftSlice.draft)

    const generateSections = async (model: string, projectId: string) => {
        let chapters = draft.story.acts.flatMap((act) => act.chapters)
        let chapterIndex = chapters.findIndex((item) => item.id === props.chapter.id) ?? -2
        let prevChapter = chapters[chapterIndex - 1]
        let nextChapter = chapters[chapterIndex + 1]
        let context = await similaritySearch({
            project: projectId,
            query: props.chapter.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: generateSectionsPrompt({
                chapter: props.chapter,
                prevChapter: prevChapter,
                nextChapter: nextChapter,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                prevSections: [],
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateChapter({chapter: {...props.chapter, sections: data.data}}))
    }

    const improveSection = async (model: string, section: DraftSection, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: props.chapter.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: improveSectionPrompt({
                section: section,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                sections: props.chapter.sections,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateSection({
            section: {
                ...section,
                title: data.data.title,
                description: data.data.description ?? "",
                scenes: []
            }
        }))
    }

    const generateSection = async (model: string, section: DraftSection, projectId: string) => {
        let context = await similaritySearch({
            project: projectId,
            query: props.chapter.description ?? '',
            where: {"$or": [{'type': 'lore'}, {'type': 'setting'}, {'type': 'shot'}]}
        })
        let data = await aiChainPrompt({
            projectId: projectId,
            prompt: generateSectionPrompt({
                section: section.id,
                genres: draft.properties.genre,
                synopsis: draft.properties.synopsis,
                sections: props.chapter.sections,
                context: context?.documents
            }),
            model: model,
            type: 'json'
        })
        dispatch(updateSection({
            section: {
                ...section,
                title: data.data.title,
                description: data.data.description ?? "",
                scenes: []
            }
        }))
    }

    return (
        <Flex style={{padding: 4, flexGrow: 1, height: "100%", overflowY: 'auto', scrollbarWidth: "none"}}
              vertical={true} gap={10}>
            <Card title={props.chapter.title}>
                <TextAreaActions
                    style={{border: "none"}}
                    value={props.chapter.description}
                    autoSize={true}
                    validate={text => true}
                    onChange={(text: string) => {
                        dispatch(updateChapter({
                            // @ts-ignore
                            chapter: {
                                ...props.chapter,
                                description: text
                            }
                        }))
                    }}
                />
            </Card>
            <Card>
                <Flex justify={'space-between'} align={'center'} wrap={'wrap'}>
                    <Title level={5} style={{margin: 0}}>Sections</Title>
                    <Space>
                        <GenButton
                            style={{width: 130}}
                            type={'primary'}
                            actions={[{
                                key: "1", title: "Generate", action: async (model: string, projectId: string) =>
                                    generateSections(model, projectId)
                                        .catch((e) => {
                                            console.log(e)
                                        })
                            }]
                            }/>
                    </Space>
                </Flex>
            </Card>

            <Space direction={'vertical'}>
                {(props.chapter.sections ?? []).map((item, index) =>
                    <Card style={{flexGrow: 1}} key={index}
                          title={<Flex justify={'space-between'}>
                              <Flex gap={10} style={{flexGrow: 1}}>
                                  <div style={{
                                      height: 30,
                                      width: 30,
                                      borderRadius: 30,
                                      background: "#ffffff",
                                      color: "#000",
                                      minWidth: 30,
                                      textAlign: "center",
                                      padding: 3
                                  }}>{index + 1}</div>
                                  <DebounceInput
                                      style={{fontWeight: 'bold'}}
                                      placeholder={"Scene title"}
                                      value={item.title}
                                      onChange={(text) => {
                                          dispatch(updateScene({
                                              // @ts-ignore
                                              scene: {
                                                  ...item,
                                                  title: text
                                              }
                                          }))
                                      }}/>
                                  <Space>
                                      <Button disabled={index == 0}
                                              style={{color: index == 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,1)'}}
                                              type={'text'}
                                              icon={<FontAwesomeIcon icon={faArrowUp}/>}
                                              onClick={() => {
                                                  let sections = [...props.chapter.sections]
                                                  let prev = {...sections[index - 1]}
                                                  let current = {...sections[index]}
                                                  let prevId = prev.id
                                                  prev.id = current.id
                                                  current.id = prevId

                                                  dispatch(updateChapter({
                                                      // @ts-ignore
                                                      chapter: {
                                                          ...props.chapter,
                                                          sections: [...sections.filter((item) => item.id !== prev.id && item.id !== current.id)].concat([prev, current]).sort((a, b) => a.id - b.id) as []
                                                      }
                                                  }))
                                              }}/>
                                      <Button disabled={index == props.chapter.sections.length - 1}
                                              style={{color: index == props.chapter.sections.length - 1 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,1)'}}
                                              type={'text'}
                                              icon={<FontAwesomeIcon icon={faArrowDown}/>} onClick={() => {
                                          let sections = [...props.chapter.sections]
                                          let next = {...sections[index + 1]}
                                          let current = {...sections[index]}
                                          let nextId = next.id
                                          next.id = current.id
                                          current.id = nextId

                                          dispatch(updateChapter({
                                              // @ts-ignore
                                              chapter: {
                                                  ...props.chapter,
                                                  sections: [...sections.filter((item) => item.id !== next.id && item.id !== current.id)].concat([current, next]).sort((a, b) => a.id - b.id) as []
                                              }
                                          }))
                                      }}/>
                                      <Button icon={<FontAwesomeIcon icon={faTrash}/>} onClick={() => {

                                          dispatch(updateChapter({
                                              // @ts-ignore
                                              chapter: {
                                                  ...props.chapter,
                                                  sections: props.chapter.sections.filter((it) => it.id !== item.id) as []
                                              }
                                          }))
                                      }}/>
                                      <GenButton
                                          mode={'icon'}
                                          hideMainAction={true}
                                          icon={<IconBoltGrad/>}
                                          actions={[{
                                              key: "1",
                                              title: "Re-generate",
                                              action: async (model: string, projectId: string) => generateSection(model, item, projectId),
                                              render: (key, title, action) => {
                                                  return <Space><BoltIcon secondary={faRotate} size={16}/>{title}
                                                  </Space>
                                              }
                                          }, {
                                              key: "2",
                                              title: "Improve",
                                              action: async (model: string, projectId: string) => improveSection(model, item, projectId),
                                              render: (key, title, action) => {
                                                  return <Space><BoltIcon secondary={faArrowUpWideShort}
                                                                          size={16}/>{title}</Space>
                                              }
                                          }]}/>
                                  </Space>
                              </Flex>
                          </Flex>}>

                        <TextAreaActions
                            style={{border: "none"}}
                            value={item.description}
                            autoSize={true}
                            validate={text => true}
                            onChange={(text: string) => {
                                dispatch(updateSection({
                                    // @ts-ignore
                                    section: {
                                        ...item,
                                        description: text
                                    }
                                }))
                            }}
                        />
                    </Card>
                )}
            </Space>
        </Flex>
    )
}